import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { PendingRequestsService } from '../services/pending-requests.service';
import { ISendToBank } from '../models/send-to-bank.model';
import { firstValueFrom } from 'rxjs';
import { ILoggedInUser } from '../../interfaces/common/loggedInUser';
import { LoggedInUserService } from '../../services/common/logged-in-user.service';
import { SearchRequestService } from '../services/search-request.service';
import { IRequestSendToJde } from '../models/request-send-to-jde.model';
import { IRequest } from '../models/request.model';

@Component({
  selector: 'app-send-to-bank',
  standalone: true,
  imports: [MatDialogModule, MatTableModule, CommonModule],
  templateUrl: './send-to-bank.component.html',
  styleUrl: './send-to-bank.component.scss'
})
export class SendToBankComponent implements OnInit {
  requests: {requestNumber: number, title: string}[] = [];
  displayedColumns: string[] = ['number', 'requestNumber', 'status'];
  pendingRequestsService: PendingRequestsService = inject(PendingRequestsService);
  masterAccount: string;
  mainMessage: string = "";
  mainData: ISendToBank[] = [];
  user: ILoggedInUser;

  constructor(@Inject(MAT_DIALOG_DATA) public data: {requests: {requestNumber: number, title: string}[]},
      private userService: LoggedInUserService,
      private searchRequestService: SearchRequestService, 
      public dialogRef: MatDialogRef<SendToBankComponent>) { 
    this.requests = data.requests;
  }

  async ngOnInit(): Promise<void> {
    this.userService.currentUser$.subscribe(data => {
      this.user = data;
    });

    this.requests.forEach(request => {
      this.mainData.push({ requestNumber: request.requestNumber, status: 'Processing...',statusJde: "", title: request.title });
    });

    try {
      const data = await firstValueFrom(this.pendingRequestsService.getBankAccount(371));
      this.masterAccount = data.account;
      for (const item of this.mainData) {
        await this.sendToBank(item);
      }
    } catch (error) {
      this.mainMessage = "Could not determine the master account number.";
      this.setProcessingStopped(this.mainData);
    }
}

  setProcessingStopped(data: ISendToBank[]): void {
    data.forEach(item => {
      item.status = 'Processing stopped.';
    });
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  sendToJde(sendToBankData: ISendToBank): void {
    sendToBankData.statusJde = 'Sending request to JDE...';
    this.searchRequestService.getRequestDetails(sendToBankData.requestNumber.toString()).subscribe(requestData => {  
      this.sendRequestToJde(requestData, sendToBankData);
    });
  }

  sendRequestToJde(requestData: IRequest, sendToBankData: ISendToBank): void {
    const requestSendToJde: IRequestSendToJde = {
      requestNumber: requestData.requestNumber,
      userEmail: this.user.userEmail,
      userName: this.user.userName,
      modeCode: requestData.modeCode,
      howPaidCode: requestData.howPaidCode,
      employeeNo: requestData.employeeNo,
      totalAmount: requestData.totalAmountNumber,
      requestTypeDescription: requestData.requestType,
      requestType: requestData.requestTypeId,
      enteredByEmail: requestData.enteredByEmail,
      employeeEmail: requestData.employeeEmail,
      employeeName: requestData.employeeName,
      reconId: requestData.reconId,
      fromGO165: requestData.fromGO165,
      logOnName: this.user.logOnName
    };
    
    this.searchRequestService.requestSendToJde(requestSendToJde).subscribe({
      next: () => {
        sendToBankData.statusJde = 'Request sent to JDE and Closed.';
      },
      error: (error) => {
        sendToBankData.statusJde = 'Error sending request to JDE';
        console.error("Error sending request to JDE:", error);
      }
    });
  }

  async sendToBank(data: ISendToBank): Promise<void> {
    try {
      const resultInfo = await firstValueFrom(this.pendingRequestsService.validateBankRequest(data));
      if (resultInfo.isFailure) {
        data.status = resultInfo.error.description;
      } else {
        data.status = 'Request successfully validated.';
        try {
          data.userEmail = this.user.userEmail;
          data.userName = this.user.userName;
          const result = await firstValueFrom(this.pendingRequestsService.sendRequestToBank(data));
          if (result.isFailure) {
            data.status = result.error.description;
          } else {
            this.sendToJde(data);
            data.status = 'Request sent to bank.';
          }
        } catch (error) {
          console.error("Error sending request to bank:", error);
          data.status = 'An error occurred while sending request to bank.';
        }
      }
    } catch (error) {
      console.error("Error validating request:", error);
      data.status = 'An error occurred during validation.';
    }
  }
}